import { useState, useEffect, useCallback, useMemo } from 'react';

import debounce from 'lodash/debounce';
import Select from 'react-select';

import { useFilter } from '@features/table-filter';
import ErrorMessage from '@shared/components/ErrorMessage';
import { reactSelectStyles } from '@shared/components/Form';
import { IDataResponse, TFetchQueryError } from '@shared/types/fetch-data';
import { IZodValidationError } from '@shared/zod';

interface ISelectAsyncOption {
  value: string;
  label: string;
}

interface IProps<T, Res extends IDataResponse<T>> {
  name: string;
  data: Res;
  isStatic?: boolean;
  getOptionLabel?: (option: T) => string;
  getOptionValue?: (option: T) => string;
  selectedValue?: string;
  onChange?: (value: string) => void;
  isLoading?: boolean;
  isDisabled?: boolean;
  filter?: ReturnType<typeof useFilter>;
  error?: IZodValidationError | TFetchQueryError;
  filterKey?: string;
}

const DEBOUNCE_DELAY = 300;
const DEBOUNCE_OPTIONS = { leading: true };

export const SelectAsync = <T, Res extends IDataResponse<T>>({
  name,
  data,
  isStatic = false,
  filterKey = 'name',
  getOptionLabel,
  getOptionValue,
  selectedValue,
  onChange,
  filter,
  isLoading = false,
  error,
  isDisabled = false,
}: IProps<T, Res>) => {
  const [inputValue, setInputValue] = useState('');
  const [hasMore, setHasMore] = useState(true);
  const [options, setOptions] = useState<ISelectAsyncOption[]>([]);
  const [selectedOption, setSelectedOption] = useState<ISelectAsyncOption | null>(null);
  const [userHasInteracted, setUserHasInteracted] = useState(false);

  // Static search, bypassing API call
  const filteredOptions = useMemo(() => {
    if (!isStatic || !inputValue.trim()) return options;

    const searchValue = inputValue.toLowerCase();
    return options.filter(
      option =>
        option.label.toLowerCase().includes(searchValue) ||
        option.value.toLowerCase().includes(searchValue),
    );
  }, [isStatic, options, inputValue]);

  const setQuery = useCallback(
    (query: string) => {
      if (!isStatic && filter?.updateFilter) {
        filter.updateFilter({ [`filter[${filterKey}]`]: query });
      }
    },
    [filter, filterKey, isStatic],
  );

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedAsyncOptions = useCallback(
    debounce(
      (input: string) => {
        if (setQuery) setQuery(input);
      },
      DEBOUNCE_DELAY,
      DEBOUNCE_OPTIONS,
    ),
    [setQuery],
  );

  useEffect(() => {
    if (data) {
      const newOptions = data.data.map(item => ({
        value: getOptionValue?.(item) || '',
        label: getOptionLabel?.(item) || '',
      }));

      setOptions(prevOptions => {
        if (isStatic || data.meta.current_page === 1) {
          return newOptions;
        }
        const combinedOptions = [...prevOptions, ...newOptions];
        const uniqueOptions = combinedOptions.filter(
          (option, index, self) => index === self.findIndex(o => o.value === option.value),
        );
        return uniqueOptions;
      });

      if (!isStatic) {
        setHasMore(!!data.links.next);
      }
    }
  }, [data, getOptionLabel, getOptionValue, isStatic]);

  useEffect(() => {
    if (selectedValue && options.length > 0 && !userHasInteracted) {
      const selected = options.find(option => String(option.value) === selectedValue) || null;
      setSelectedOption(selected);
    } else if (!selectedValue && !userHasInteracted) {
      setSelectedOption(null);
    }
  }, [selectedValue, options, userHasInteracted]);

  useEffect(() => {
    return () => debouncedAsyncOptions.cancel();
  }, [debouncedAsyncOptions]);

  const handleScrollToBottom = useCallback(() => {
    if (!isStatic && hasMore && filter?.setPage) {
      filter.setPage(prevPage => (Number(prevPage) + 1).toString());
    }
  }, [hasMore, filter, isStatic]);

  const handleChange = useCallback(
    (selected: ISelectAsyncOption | null) => {
      setSelectedOption(selected);
      setUserHasInteracted(true);
      onChange?.(selected?.value ?? '');
    },
    [onChange],
  );

  const handleInputChange = useCallback(
    (input: string) => {
      setInputValue(input);
      if (input.trim() === '') {
        setQuery('');
        return;
      }
      if (!isStatic) {
        debouncedAsyncOptions(input);
      }
    },
    [debouncedAsyncOptions, setQuery, isStatic],
  );

  if (error) {
    return <ErrorMessage error={error} />;
  }

  return (
    <>
      <Select
        classNamePrefix='react-select'
        className='react-select-container'
        inputValue={inputValue}
        isLoading={isLoading}
        isClearable
        hideSelectedOptions={false}
        maxMenuHeight={200}
        menuPosition='fixed'
        isDisabled={isDisabled}
        styles={reactSelectStyles<ISelectAsyncOption, false>()}
        menuShouldBlockScroll={true}
        menuShouldScrollIntoView={false}
        onChange={handleChange}
        onInputChange={handleInputChange}
        options={isStatic ? filteredOptions : options}
        value={selectedOption}
        closeMenuOnSelect
        onMenuScrollToBottom={handleScrollToBottom}
        noOptionsMessage={({ inputValue }) =>
          inputValue ? 'No options found' : 'No options available'
        }
      />
      <input type='hidden' name={name} value={selectedOption?.value ?? ''} aria-hidden='true' />
    </>
  );
};
